/**
 * What this Word can do. The pane already falls back wherever a feature is
 * missing; this is the one place that says so plainly, reached from the note
 * on the Tender tab.
 */
import { ArrowLeftIcon } from "@/design/icons";
import type { FC } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";

import { pageSetupSupported } from "../document/pageSetup";
import { documentCapabilities } from "../document/snapshot";
import { canImportTemplateStyles } from "../document/templateStyles";

type Row = { name: string; ok: boolean | "partly"; note: string };

export const hostName = (): string => {
  try {
    const p = Office.context.diagnostics?.platform;
    const platform =
      p === Office.PlatformType.OfficeOnline
        ? "Word on the web"
        : p === Office.PlatformType.Mac
          ? "Word for Mac"
          : p === Office.PlatformType.iOS
            ? "Word for iPad"
            : "Word for Windows";
    const version = Office.context.diagnostics?.version;
    return version && p !== Office.PlatformType.OfficeOnline ? `${platform} ${version}` : platform;
  } catch {
    return "Word";
  }
};

export const capabilityRows = (): Row[] => {
  const caps = documentCapabilities();
  return [
    { name: "Chat, reading and search", ok: true, note: "Available" },
    { name: "Tender tab, status, get and send", ok: true, note: "Available" },
    {
      name: "Edits as tracked changes",
      ok: caps.tracked_changes,
      note: caps.tracked_changes ? "Available" : "Edits go straight in; Undo works",
    },
    { name: "Comments", ok: caps.comments, note: caps.comments ? "Available" : "Findings appear in the chat instead" },
    {
      name: "Accept or reject io.ai's changes from the pane",
      ok: caps.review,
      note: caps.review ? "Available" : "Use Word's Review tab",
    },
    {
      name: "Apply template styles",
      ok: canImportTemplateStyles() ? true : "partly",
      note: canImportTemplateStyles() ? "Available" : "io.ai formats style by style instead",
    },
    {
      name: "Page setup",
      ok: pageSetupSupported(),
      note: pageSetupSupported() ? "Available" : "io.ai tells you the values to set",
    },
  ];
};

/** A one-line note for the Tender tab when something that matters is missing. */
export const limitsNote = (): string | null => {
  const caps = documentCapabilities();
  if (!caps.tracked_changes)
    return "This Word can't track changes, so io.ai's edits go straight into the document; Undo still works.";
  if (!caps.comments) return "This Word can't take comments, so io.ai's review findings appear in the chat.";
  return null;
};

export const CapabilitiesSheet: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
    <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
      {open && (
        <div className="flex h-full flex-col bg-surface">
          <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
            <button
              type="button"
              aria-label="Back"
              onClick={onClose}
              className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
            >
              <ArrowLeftIcon className="size-4" />
            </button>
            <span className="text-sm font-medium text-fg-3">What this Word can do</span>
          </div>
          <div className="px-4 pb-2 pt-4">
            <SheetTitle className="text-lg font-semibold leading-snug text-fg">{hostName()}</SheetTitle>
            <SheetDescription className="mt-0.5 text-sm text-fg-3">
              Everything else in io.ai works as normal.
            </SheetDescription>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {capabilityRows().map((r, i) => (
              <div key={r.name} className={cn("flex items-center gap-2.5 px-4 py-2", i && "border-t border-line/70")}>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{r.name}</span>
                  <span className="block text-xs text-fg-3">{r.note}</span>
                </span>
                <span className={cn("text-xs font-semibold", r.ok === true ? "text-status-complete" : "text-fg-4")}>
                  {r.ok === true ? "Yes" : r.ok === "partly" ? "Partly" : "No"}
                </span>
              </div>
            ))}
          </div>
          <div className="shrink-0 border-t border-line px-4 py-3 text-xs text-fg-3">
            Microsoft 365 on Windows or Mac has all of these.
          </div>
        </div>
      )}
    </SheetContent>
  </Sheet>
);
